import { useQuery } from "@tanstack/react-query";
import type { PageContent, SiteSetting, SeoMeta, Banner } from "@shared/schema";
import { CACHE_TIMES as SHARED_CACHE_TIMES } from "@/lib/queryClient";

// Local CMS-specific staleTime selection. We map each CMS resource to one
// of the shared tiers documented in `client/src/lib/queryClient.ts` and
// `docs/audits/cache-strategy.md`. Keep aligned with `Layout` and
// `PublicLayoutShell`: components polling the same key MUST agree on
// staleTime, otherwise React Query refetches more often than necessary.
const CACHE_TIMES = {
  content: SHARED_CACHE_TIMES.fresh,    // 30s — page content edited frequently
  settings: SHARED_CACHE_TIMES.catalog, // 5 min — site-wide settings (rarely change)
  seo: SHARED_CACHE_TIMES.catalog,      // 5 min — SEO metadata (rarely change)
  banners: SHARED_CACHE_TIMES.fresh,    // 30s — banner devono apparire/sparire rapidamente
};

/**
 * Hook to fetch page content from CMS
 * @param pageSlug - The page slug to fetch content for
 * @returns Page content array with fallback support
 */
export function usePageContent(pageSlug: string) {
  return useQuery<PageContent[]>({
    queryKey: [`/api/crm/cms/pages/${pageSlug}`],
    staleTime: CACHE_TIMES.content,
    gcTime: 60 * 1000, // 1 minute garbage collection time
    refetchOnWindowFocus: true, // Refetch when window gains focus
    refetchOnMount: true, // Refetch when component mounts
    retry: 1, // Only retry once to avoid delays
  });
}

/**
 * Hook to fetch a single setting from CMS
 * @param settingKey - The setting key to fetch
 * @returns The setting value or undefined
 */
export function useSetting(settingKey: string) {
  const { data, ...rest } = useQuery<SiteSetting>({
    queryKey: [`/api/crm/cms/settings/${settingKey}`],
    staleTime: CACHE_TIMES.settings,
    gcTime: 2 * 60 * 1000, // 2 minutes garbage collection
    refetchOnWindowFocus: true, // Refetch when window gains focus
    refetchOnMount: true, // Refetch when component mounts
    retry: 1,
  });
  
  return {
    data: data?.settingValue,
    ...rest
  };
}

/**
 * Hook to fetch multiple settings at once (uses public endpoint)
 * @param settingKeys - Array of setting keys to fetch
 * @returns Object with setting values keyed by setting key
 */
export function useSettings(settingKeys: string[]) {
  // Fetch all public settings from public endpoint (no auth required)
  const { data: allSettings, ...rest } = useQuery<Record<string, string>>({
    queryKey: ["/api/public/settings"],
    staleTime: CACHE_TIMES.settings,
    gcTime: 2 * 60 * 1000, // 2 minutes garbage collection
    refetchOnWindowFocus: true, // Refetch when window gains focus
    refetchOnMount: true, // Refetch when component mounts
    retry: 1,
  });
  
  // Filter to only requested keys
  const settings = settingKeys.reduce((acc, key) => {
    acc[key] = allSettings?.[key];
    return acc;
  }, {} as Record<string, any>);
  
  return {
    data: settings,
    ...rest
  };
}

/**
 * Hook to fetch SEO metadata for a page
 * @param pageSlug - The page slug to fetch SEO data for (e.g., "/", "/chi-siamo")
 * @returns SEO metadata for the page
 */
export function useSeoMeta(pageSlug: string) {
  return useQuery<SeoMeta | null>({
    queryKey: ['/api/crm/cms/seo/page', pageSlug],
    queryFn: async () => {
      const response = await fetch(`/api/crm/cms/seo/page/${encodeURIComponent(pageSlug)}`);
      if (!response.ok) {
        if (response.status === 404) {
          return null; // No SEO data for this page, use fallback
        }
        throw new Error('Failed to fetch SEO data');
      }
      return response.json();
    },
    staleTime: CACHE_TIMES.seo,
    gcTime: 10 * 60 * 1000, // 10 minutes garbage collection (SEO can be cached longer)
    refetchOnWindowFocus: true, // Refetch when window gains focus
    refetchOnMount: true, // Refetch when component mounts
    retry: 1,
  });
}

/**
 * Hook to fetch active banners
 * @returns Array of active banners
 */
export function useActiveBanners() {
  return useQuery<Banner[]>({
    queryKey: ["/api/crm/cms/banners/active"],
    staleTime: CACHE_TIMES.banners,
    gcTime: 60 * 1000, // 1 minute garbage collection
    refetchOnWindowFocus: true, // Refetch when window gains focus
    refetchOnMount: true, // Refetch when component mounts
    retry: 1,
  });
}

/**
 * Helper function to get content with fallback
 * @param contents - Array of page contents
 * @param sectionKey - The section key to find
 * @param fallback - Fallback value if content not found
 * @returns The content or fallback value
 */
export function getContentWithFallback(
  contents: PageContent[] | undefined,
  sectionKey: string,
  fallback: string
): string {
  const content = contents?.find(c => c.sectionKey === sectionKey);
  return content?.content || fallback;
}

/**
 * Helper function to get setting with fallback
 * @param settings - Settings object
 * @param key - Setting key
 * @param fallback - Fallback value
 * @returns The setting value or fallback
 */
export function getSettingWithFallback<T>(
  settings: Record<string, any> | undefined,
  key: string,
  fallback: T
): T {
  return settings?.[key] ?? fallback;
}